import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { useTRPC } from '@lib/trpc'

export const useTeam = (slug: string | undefined) => {
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.teams.get.queryOptions({ team: slug ?? '' }), enabled: Boolean(slug) })
  return { data: query.data, teamLoading: query.isPending, teamError: query.error, refetchTeam: query.refetch }
}

export const useCheckSlug = (slug: string, teamId?: string) => {
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.teams.checkSlug.queryOptions({ slug, teamId }), enabled: slug.length > 1 })
  return { slugCheck: query.data, slugCheckLoading: query.isFetching }
}

export const useCreateTeamMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    trpc.teams.create.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
    })
  )
  return { createTeam: mutation.mutateAsync, createTeamIsPending: mutation.isPending }
}

export const useUpdateTeamMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    trpc.teams.update.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.teams.get.queryKey() })
      }
    })
  )
  return { updateTeam: mutation.mutateAsync, updateTeamIsPending: mutation.isPending }
}

export const useUploadTeamLogoMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    trpc.teams.uploadLogo.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.teams.get.queryKey() })
      }
    })
  )
  return { uploadTeamLogo: mutation.mutateAsync, uploadTeamLogoIsPending: mutation.isPending }
}

export const useRemoveTeamLogoMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    trpc.teams.removeLogo.mutationOptions({
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
        queryClient.invalidateQueries({ queryKey: trpc.teams.get.queryKey() })
      }
    })
  )
  return { removeTeamLogo: mutation.mutateAsync, removeTeamLogoIsPending: mutation.isPending }
}

export const useDeleteTeamMutation = () => {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const mutation = useMutation(
    trpc.teams.delete.mutationOptions({
      onSuccess: () => queryClient.invalidateQueries({ queryKey: trpc.auth.me.queryKey() })
    })
  )
  return { deleteTeam: mutation.mutateAsync, deleteTeamIsPending: mutation.isPending }
}

export const useSuggestedTeamName = () => {
  const trpc = useTRPC()
  const query = useQuery({ ...trpc.teams.suggestName.queryOptions(), staleTime: 60_000 })
  return { suggestedName: query.data?.name ?? '', suggestedNameLoading: query.isPending }
}
